import type { WorkflowEdge, WorkflowGraphDefinition, WorkflowNode } from '../../services/workflowsApi.ts';
import { connectionError } from './workflowPresentation.ts';

type NodeChange = { before: WorkflowNode; after: WorkflowNode };
type PortChange = { before: WorkflowEdge[]; after: WorkflowEdge[] };
export interface InspectorDraft { nodes: Record<string, NodeChange>; ports: Record<string, PortChange> }
export const emptyInspectorDraft = (): InspectorDraft => ({ nodes: {}, ports: {} });
export const hasInspectorDraft = (draft: InspectorDraft) => Boolean(Object.keys(draft.nodes).length || Object.keys(draft.ports).length);
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const portKey = (edge: WorkflowEdge) => JSON.stringify([edge.source, edge.source_port]);
const ports = (graph: WorkflowGraphDefinition) => graph.edges.reduce<Record<string, WorkflowEdge[]>>((result, edge) => {
  const key = portKey(edge); result[key] = [...(result[key] || []), edge]; return result;
}, {});

/** Track only this edit, so subsequent viewport or other-node edits stay external. */
export function stageInspectorDraft(current: WorkflowGraphDefinition, next: WorkflowGraphDefinition, previous: InspectorDraft): InspectorDraft {
  const nodes = { ...previous.nodes }, changedPorts = { ...previous.ports };
  current.nodes.forEach(before => {
    const after = next.nodes.find(node => node.id === before.id);
    if (!after || after.type !== before.type || equal(before.config, after.config)) return;
    const original = nodes[before.id]?.before || before;
    if (equal(original.config, after.config)) delete nodes[before.id];
    else nodes[before.id] = { before: original, after };
  });
  const oldPorts = ports(current), newPorts = ports(next);
  new Set([...Object.keys(oldPorts), ...Object.keys(newPorts)]).forEach(key => {
    const before = oldPorts[key] || [], after = newPorts[key] || [];
    if (equal(before, after)) return;
    const original = changedPorts[key]?.before || before;
    if (equal(original, after)) delete changedPorts[key];
    else changedPorts[key] = { before: original, after };
  });
  return { nodes, ports: changedPorts };
}

/** Merge onto the latest graph; conflicts never overwrite a concurrent configuration. */
export function mergeInspectorDraft(current: WorkflowGraphDefinition, draft: InspectorDraft): { graph: WorkflowGraphDefinition; error: string | null } {
  const currentPorts = ports(current);
  for (const [id, change] of Object.entries(draft.nodes)) {
    const node = current.nodes.find(item => item.id === id);
    if (!node || node.type !== change.before.type || (!equal(node.config, change.before.config) && !equal(node.config, change.after.config))) {
      return { graph: current, error: 'A configuração desta etapa mudou. Revise o rascunho antes de aplicar.' };
    }
  }
  for (const [key, change] of Object.entries(draft.ports)) {
    const now = currentPorts[key] || [];
    if (!equal(now, change.before) && !equal(now, change.after)) return { graph: current, error: 'Um destino mudou durante a edição. Revise as conexões antes de aplicar.' };
    if (change.after.some(edge => !current.nodes.some(node => node.id === edge.source) || (edge.target && !current.nodes.some(node => node.id === edge.target)))) {
      return { graph: current, error: 'Uma etapa deste caminho foi removida. Revise o destino antes de aplicar.' };
    }
  }
  const applied = new Set<string>();
  const edges = current.edges.flatMap(edge => {
    const key = portKey(edge), change = draft.ports[key];
    if (!change) return [edge];
    if (applied.has(key)) return [];
    applied.add(key); return change.after;
  });
  Object.entries(draft.ports).forEach(([key, change]) => { if (!applied.has(key)) edges.push(...change.after); });
  const graph = { ...current, nodes: current.nodes.map(node => draft.nodes[node.id]
    ? { ...node, config: draft.nodes[node.id].after.config } as WorkflowNode : node), edges };
  for (const change of Object.values(draft.ports)) {
    for (const edge of change.after) {
      const error = connectionError(graph, edge.source, edge.source_port, edge.target);
      if (error) return { graph: current, error };
    }
  }
  return { graph, error: null };
}
